import { UploadOutlined } from "@ant-design/icons";
import { Form, Upload } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { canAccess } from "../../../../core/router/auth/permissions";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { showWarningNotification } from "../../../../core/setup/redux/state/notifications/notificationsState";
import { SecondaryButton } from "../../../Buttons/components/ActionButton";
import type { AntCustomFormField } from "../../types/types";

export interface CsvTerminalsImportProps extends AntCustomFormField<undefined> {
    readOnly?: boolean;
    targetField: string;
}

const parseCsv = async (file: File): Promise<string[]> => {
    const text = await file.text();
    return text
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
};

/**
 * CSV import component for uploading terminal IDs.
 * Parses CSV file and updates both the textarea and terminals table fields.
 */
export const CsvTerminalsImport: FC<CsvTerminalsImportProps> = ({ readOnly, targetField }) => {
    const form = Form.useFormInstance();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const hasAccess = canAccess(ResourceName.CAMPAIGNS, "csvImport");

    const beforeUpload = async (file: File) => {
        if (!targetField) return false;
        try {
            const terminalIds = await parseCsv(file);
            const content = terminalIds.join("\n");

            // Determine the corresponding terminals field name (terminals1 or terminals2)
            // by replacing "textareaTerminalsInput" with "terminals"
            const terminalsFieldName = targetField.replace("textareaTerminalsInput", "terminals");

            // Update both the textarea field and the terminals table field
            form.setFieldsValue({
                [targetField]: content,
                [terminalsFieldName]: terminalIds,
            });
        } catch (error) {
            dispatch(
                showWarningNotification({
                    text: t("error.campaign.csvImport"),
                    autoCloseDisabled: true,
                }),
            );
        }
        return false;
    };

    if (!hasAccess) {
        return null;
    }

    return (
        <Upload accept=".csv,text/csv" beforeUpload={beforeUpload} showUploadList={false} disabled={readOnly}>
            <SecondaryButton icon={<UploadOutlined />} disabled={readOnly}>
                {i18n.t("button.importTerminalsCsv")}
            </SecondaryButton>
        </Upload>
    );
};
